import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { IconCheck, IconClose, IconDown, IconEdit, IconPlus } from '@arco-design/web-react/icon';
import { CompanionProfile, getCompanionDisplayName, isPresetCompanion } from '@/lib/useCompanions';
import styles from './NormalCompanionPicker.module.less';

type Props = {
  profiles: CompanionProfile[];
  activeId?: string;
  name: string;
  avatar: string;
  description: string;
  disabled: boolean;
  canAddMore: boolean;
  onSelect: (id: string) => void;
  onEdit: () => void;
  onAdd: () => void;
  onDelete: (id: string) => void;
};

export default function NormalCompanionPicker({
  profiles,
  activeId,
  name,
  avatar,
  description,
  disabled,
  canAddMore,
  onSelect,
  onEdit,
  onAdd,
  onDelete,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const expanded = open && !disabled;

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!expanded) return;
    const selected = panelRef.current?.querySelector<HTMLButtonElement>('[aria-pressed="true"]');
    (selected ?? panelRef.current?.querySelector<HTMLButtonElement>('button'))?.focus();
    selected?.scrollIntoView({ block: 'nearest' });
    const closeOutside = (event: Event) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('focusin', closeOutside);
    document.addEventListener('keydown', closeEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('focusin', closeOutside);
      document.removeEventListener('keydown', closeEscape);
    };
  }, [expanded]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const moveFocus = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const offsets: Record<string, number> = {
      ArrowRight: 1,
      ArrowLeft: -1,
      ArrowDown: 3,
      ArrowUp: -3,
    };
    const offset = offsets[event.key];
    if (offset === undefined && event.key !== 'Home' && event.key !== 'End') return;
    event.preventDefault();
    const next =
      event.key === 'Home' ? 0 : event.key === 'End' ? profiles.length - 1 : index + offset;
    panelRef.current
      ?.querySelectorAll<HTMLButtonElement>('[data-companion-choice]')
      [Math.max(0, Math.min(profiles.length - 1, next))]?.focus();
  };

  return (
    <div ref={rootRef} className={styles.root}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger} ${expanded ? styles.triggerOpen : ''}`}
        disabled={disabled}
        aria-label={`切换伙伴：${name}`}
        aria-haspopup="dialog"
        aria-expanded={expanded}
        aria-controls={expanded ? panelId : undefined}
        title={disabled ? '结束当前通话后可切换伙伴' : '切换伙伴'}
        onClick={() => setOpen((value) => !value)}
      >
        <span
          className={`${styles.avatar} ${
            activeId === 'preset_einstein' ? styles.einsteinAvatar : ''
          }`}
          aria-hidden="true"
        >
          {avatar ? (
            <img src={avatar} alt="" />
          ) : (
            <span>{name.trim().slice(0, 1).toUpperCase()}</span>
          )}
        </span>
        <span className={styles.details}>
          <span className={styles.nameRow}>
            <span className={styles.name}>{name}</span>
            <IconDown className={styles.chevron} />
          </span>
        </span>
      </button>
      <span className={styles.description}>{description}</span>
      {profiles.length > 0 && (
        <button
          type="button"
          className={styles.edit}
          disabled={disabled}
          aria-label={`编辑角色：${name}`}
          title="编辑角色"
          onClick={() => {
            setOpen(false);
            onEdit();
          }}
        >
          <IconEdit />
        </button>
      )}
      {expanded && (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label="选择伙伴"
          className={styles.panel}
        >
          <div className={styles.heading}>
            <span>选择伙伴</span>
            <span>{profiles.length} 位</span>
          </div>
          <div className={styles.list}>
            {profiles.map((profile, index) => {
              const active = profile.id === activeId;
              const displayName = getCompanionDisplayName(profile);
              return (
                <div key={profile.id} className={`${styles.item} ${active ? styles.active : ''}`}>
                  <button
                    type="button"
                    className={styles.choice}
                    data-companion-choice
                    aria-label={`选择${displayName}`}
                    aria-pressed={active}
                    title={displayName}
                    onKeyDown={(event) => moveFocus(event, index)}
                    onClick={() => {
                      onSelect(profile.id);
                      close();
                    }}
                  >
                    <span
                      className={`${styles.portrait} ${
                        profile.id === 'preset_einstein' ? styles.einsteinAvatar : ''
                      }`}
                      aria-hidden="true"
                    >
                      {profile.avatar ? (
                        <img src={profile.avatar} alt="" />
                      ) : (
                        <span>{displayName.trim().slice(0, 1).toUpperCase()}</span>
                      )}
                    </span>
                    <span className={styles.choiceName}>{displayName}</span>
                    {active && <IconCheck className={styles.check} />}
                  </button>
                  {!isPresetCompanion(profile) && (
                    <button
                      type="button"
                      className={styles.delete}
                      aria-label={`删除${displayName}`}
                      title={`删除${displayName}`}
                      onClick={() => {
                        close();
                        onDelete(profile.id);
                      }}
                    >
                      <IconClose />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <div className={styles.footer}>
            <button
              type="button"
              className={styles.add}
              disabled={!canAddMore}
              onClick={() => {
                setOpen(false);
                onAdd();
              }}
            >
              <IconPlus />
              <span>新增角色</span>
              {!canAddMore && <small>已达上限</small>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
